import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  COMMAND_PRIORITY_EDITOR,
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  $createRangeSelection,
  $getRoot,
  RangeSelection,
  createCommand,
  LexicalCommand,
  KEY_ESCAPE_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import { Icon } from '../../../../Icon';
import { ToolbarButton } from '../common';

// Проверка, является ли выделение полным
const isFullSelection = (selection: RangeSelection) => {
  try {
    const root = $getRoot();
    const anchor = selection.anchor;
    const focus = selection.focus;

    const rootTextSize = root.getTextContentSize();
    const selectedTextSize = selection.getTextContent().length;

    return (
      (rootTextSize === selectedTextSize && rootTextSize !== 0) ||
      (anchor.key === root.getKey() &&
        focus.key === root.getKey() &&
        anchor.offset === 0 &&
        focus.offset === root.getChildrenSize())
    );
  } catch {
    return false;
  }
};

const TOGGLE_SELECT_ALL_COMMAND: LexicalCommand<void> = createCommand();

export const SelectAllPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const [selectionState, setSelectionState] = useState<'none' | 'full'>('none');
  const isSelectAllActive = useRef(false);
  const lastSelectionRef = useRef<null | RangeSelection>(null);

  const isSelected = useMemo(() => selectionState === 'full', [selectionState]);

  const handleClick = () => {
    editor.dispatchCommand(TOGGLE_SELECT_ALL_COMMAND, undefined);
  };

  // Регистрация команды для выделения/снятия выделения
  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_SELECT_ALL_COMMAND,
      () => {
        editor.update(() => {
          const root = $getRoot();

          if (isSelectAllActive.current || isSelected) {
            // Снимаем выделение
            $setSelection(null);
            isSelectAllActive.current = false;
          } else {
            // Выделяем весь текст
            const selection = $createRangeSelection();
            selection.anchor.set(root.getKey(), 0, 'element');
            selection.focus.set(
              root.getKey(),
              root.getChildrenSize(),
              'element',
            );
            $setSelection(selection);
            isSelectAllActive.current = true;
          }
        });

        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, isSelected]);

  // Отслеживание изменений выделения
  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.update(() => {
          const currentSelection = $getSelection();

          if ($isRangeSelection(currentSelection)) {
            if (currentSelection.isCollapsed()) {
              isSelectAllActive.current = false;
            }

            lastSelectionRef.current = currentSelection.clone();
          }
        });

        return false;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  // Обработка Escape для снятия выделения
  useEffect(() => {
    return editor.registerCommand(
      KEY_ESCAPE_COMMAND,
      () => {
        if (isSelectAllActive.current) {
          editor.update(() => $setSelection(null));
          isSelectAllActive.current = false;
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  //Подписка на изменения, чтобы менять статус выделенного текста в состоянии компонента
  useEffect(() => {
    const unsubscribe = editor.registerUpdateListener(() => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setSelectionState('none');
          return;
        }

        if (isFullSelection(selection)) {
          setSelectionState('full');
        } else if (!selection.isCollapsed()) {
          setSelectionState('none');
        } else {
          setSelectionState('none');
        }
      });
    });

    return () => unsubscribe();
  }, [editor]);

  return (
    <ToolbarButton
      onClick={handleClick}
      tooltip={isSelected ? 'Снять выделение' : 'Выделить всё'}
    >
      <Icon
        name="text_viewfinder_outline"
        size="16"
        color={isSelected ? 'original.brand.primary' : 'text.light.primary'}
      />
    </ToolbarButton>
  );
};
